import { ReactNode, useCallback, useMemo } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, SxProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChildrenProps, LoadingButton } from '@dfl/mui-react-common';

type ConfirmActionProps = ChildrenProps & {
  open: boolean;
  onClose?: () => void;
  sxConfirm?: SxProps;
  onConfirm?: () => void;
  title?: string | ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  isLoading?: boolean;
  confirmation?: string | ReactNode;
  confirmButtonText?: string;
  cancelButtonText?: string;
};

const ConfirmAction = ({
  open,
  sxConfirm,
  onClose,
  maxWidth,
  children,
  title = 'common:confirmation.title',
  confirmation = 'common:confirmation.description',
  confirmButtonText = 'common:confirmation.confirm',
  cancelButtonText = 'common:cancel',
  onConfirm,
  isLoading,
}: ConfirmActionProps) => {
  const { t } = useTranslation('common');

  const titleComp = useMemo(() => (typeof title === 'string' ? t(title) : title), [title, t]);

  const confirmationText = useMemo(
    () => (typeof confirmation === 'string' ? t(confirmation) : confirmation),
    [confirmation, t],
  );

  const handleDelete = useCallback(() => {
    onConfirm?.();
    onClose?.();
  }, [onConfirm, onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
    >
      <DialogTitle id='alert-dialog-title'>{titleComp}</DialogTitle>
      <DialogContent>
        <DialogContentText id='alert-dialog-description'>
          {children}
          {confirmationText}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={sxConfirm}>
        {onClose && <Button onClick={onClose}>{t(cancelButtonText)}</Button>}
        <LoadingButton
          onClick={handleDelete}
          autoFocus
          variant={'contained'}
          loading={isLoading}
          sx={{ minWidth: 100 }}
        >
          {t(confirmButtonText)}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmAction;
